import { DEFAULT_NO_LIST_PAGINATION } from 'constants/pagination';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useGetList, useShowController } from 'react-admin';
import { ShowPageTabbed } from 'shared/react-admin/Pages';
import { Tab } from 'shared/react-admin/Tab/Tab';
import { Merchant } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';

import { MerchantTab, useTabVisibility } from './hooks/useTabVisibility';
import { MerchantsShowAccessList } from './MerchantShowAccess/MerchantShowAccessList';
import { MerchantShowAccounts } from './MerchantShowAccounts/MerchantShowAccounts';
import { MerchantShowCommissionsList } from './MerchantShowCommissions/MerchantShowCommissionsList/MerchantShowCommissionsList';
import { MerchantShowRulesList } from './MerchantShowRules/MerchantShowRulesList';
import { MerchantsShowCommonInformation } from './MerchantsShowCommonInformation/MerchantsShowCommonInformation';
import { MerchantsShowCredentials } from './MerchantsShowCredentials/MerchantsShowCredentials';
import { MerchantsShowLimits } from './MerchantsShowLimits/MerchantsShowLimits';
import { useGetMerchantNotifications } from './MerchantsShowNotifications/hooks/useGetMerchantNotifications';
import { MerchantsShowNotifications } from './MerchantsShowNotifications/MerchantsShowNotifications';
import { useMerchantProviders } from './MerchantsShowSettings/hooks/useMerchantProviders';
import { MerchantsShowSettings } from './MerchantsShowSettings/MerchantsShowSettings';

export const MerchantsShow = () => {
  const translate = useTypedTranslate();

  const { record } = useShowController<Merchant>();
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const { checkTabVisible } = useTabVisibility(record);

  const { total: totalMerchantRules } = useGetList(
    `${Resources.Merchants}/${record?.originId}/gateway_rules`,
    {
      filter: {
        ...DEFAULT_NO_LIST_PAGINATION,
      },
    },
    {
      enabled: !!record?.originId && hasResourceAccess(Resources.GatewayRules),
    },
  );

  const { total: totalLimits } = useGetList(
    Resources.Limits,
    {
      filter: {
        ...DEFAULT_NO_LIST_PAGINATION,
        merchant: [record?.id],
      },
    },
    { enabled: !!record && hasResourceAccess(Resources.Limits) },
  );

  const { total: totalMerchantCredentials } = useGetList(
    Resources.MerchantCredentials,
    {
      filter: {
        ...DEFAULT_NO_LIST_PAGINATION,
        merchant: record?.id,
      },
    },
    {
      enabled: !!record && hasResourceAccess(Resources.MerchantCredentials),
    },
  );

  const { total: totalMerchantAccounts } = useGetList(
    Resources.MerchantAccounts,
    {
      filter: {
        ...DEFAULT_NO_LIST_PAGINATION,
        merchants: [record?.id],
      },
    },
    { enabled: !!record && hasResourceAccess(Resources.MerchantAccounts) },
  );

  // todo: Здесь приходит список со всеми данными, нужно доработать бэкенд
  const { total: totalMerchantProviderSettings } = useMerchantProviders(
    record?.id,
    {
      filter: {
        merchants: [record?.id],
      },
    },
    { enabled: !!record && hasResourceAccess(Resources.MerchantAccounts) },
  );

  const { total: totalMerchantAccesses } = useGetList(
    `${Resources.Merchants}/${record?.originId}/${Resources.MerchantAccesses}`,
    {
      filter: DEFAULT_NO_LIST_PAGINATION,
    },
    { enabled: !!record && hasResourceAccess(Resources.MerchantAccesses) },
  );

  const { total: totalMerchantCommissions } = useGetList(
    Resources.Commissions,
    {
      filter: {
        ...DEFAULT_NO_LIST_PAGINATION,
        merchant: record?.id,
      },
    },
    {
      enabled:
        !!record &&
        hasResourcePermissions(Resources.Commissions, [Permission.Full, Permission.Read]),
    },
  );

  const { totalItems: totalNotificationsItems } = useGetMerchantNotifications({
    merchantId: record?.id,
  });

  return (
    <ShowPageTabbed
      headerTitle={{ titleText: record?.name }}
      listName={translate('app.merchants.merchants')}
      title={translate('app.merchants.merchantManagement')}
    >
      <Tab label={translate('app.common.generalInformation')}>
        <MerchantsShowCommonInformation record={record} />
      </Tab>
      {hasResourceAccess('merchant_credentials') && (
        <Tab label={`Реквизиты (${totalMerchantCredentials || 0})`}>
          <MerchantsShowCredentials merchantId={record?.id} merchantName={record?.name} />
        </Tab>
      )}
      {hasResourceAccess('merchant_accounts') && (
        <Tab label={translate('app.merchants.accounts', { total: totalMerchantAccounts || 0 })}>
          <MerchantShowAccounts
            merchantId={record?.id}
            merchantName={record?.name}
            merchantPlatformId={record?.platform?.['@id']}
          />
        </Tab>
      )}
      {checkTabVisible(MerchantTab.ProviderSettings) &&
        hasResourceAccess(Resources.MerchantProviderSettings) && (
          <Tab
            label={translate('app.merchants.providerSettingsTotal', {
              total: totalMerchantProviderSettings || 0,
            })}
          >
            <MerchantsShowSettings merchantId={record?.id} merchantName={record?.name} />
          </Tab>
        )}
      {hasResourceAccess(Resources.Limits) && (
        <Tab label={translate('app.merchants.limits', { total: totalLimits || 0 })}>
          <MerchantsShowLimits merchantId={record?.id} merchantName={record?.name} />
        </Tab>
      )}
      {hasResourcePermissions(Resources.Commissions, [Permission.Full, Permission.Read]) && (
        <Tab
          label={translate('app.merchants.commissions', { total: totalMerchantCommissions || 0 })}
        >
          <MerchantShowCommissionsList merchantId={record?.id} />
        </Tab>
      )}
      {hasResourceAccess(Resources.GatewayRules) && (
        <Tab label={translate('app.merchants.rules', { total: totalMerchantRules || 0 })}>
          <MerchantShowRulesList merchantId={record?.originId} merchantName={record?.name} />
        </Tab>
      )}
      {hasResourceAccess('merchant_notification_settings') && (
        <Tab
          label={translate('app.merchants.notification', { total: totalNotificationsItems || 0 })}
        >
          <MerchantsShowNotifications record={record} />
        </Tab>
      )}
      {checkTabVisible(MerchantTab.Accesses) && hasResourceAccess('merchant_accesses') && (
        <Tab label={translate('app.merchants.accesses', { total: totalMerchantAccesses || 0 })}>
          <MerchantsShowAccessList merchantId={record?.id} merchantName={record?.name} />
        </Tab>
      )}
    </ShowPageTabbed>
  );
};
